/**
 * 插入流程专用的加载入口。
 *
 * 与主链路 loadImportedModel 的区别:
 * 1. 插入预览下载结果只携带待插入模型的文件入口，根 GLB 与外部引用均回到 `/download/file` 读取。
 * 2. 使用完全隔离的一次性 cache 实例，避免与主链路 cache 产生 refCount / 生命周期干扰。
 * 3. 上层使用后端返回的 GLB FileInfo 交给 loadImportedModel 复用其 analyze/plan/loadAssemblyModel/loadPartModel
 *    逻辑，不需要为插入链路 fork 一份 loader 实现。
 */
import type { FileInfo } from '@puppycad/proto'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import {
  AssemblyManifestCache,
  AssemblyModelCache,
  disposeImportedModel,
  type ImportedAssemblyReferenceResolver,
  type InsertComponentFixationMode,
  ParsedGlbCache,
  PartAssetCache,
  parseGlb,
  type BRepRegistrar,
} from '@puppycad/cad-core'
import {
  loadImportedModel,
  type LoadedImportedModel,
} from '@/services/loaders/imported-model-loader'
import { createImportedBrepRuntimeCache } from '@/services/loaders/imported-model-brep-runtime-cache'
import { ImportedModelLoadTransaction } from '@/services/loaders/imported-model-load-transaction'
import {
  downloadInsertedCadFile,
  downloadInsertedFileByFileId,
  type DownloadInsertedCadFileResult,
} from '@/services/loaders/insert-cad-download'
import {
  isInsertedCadRuntimePackageFileId,
  resolveInsertedCadKindFromFileId,
} from '@/services/loaders/insert-cad-kind'
import { loadInsertedCadRuntimePackageModel } from '@/services/loaders/insert-cad-runtime-package-model-loader'
import { logger } from '@/utils'

export interface InsertCadSourceDescriptor {
  /** `/download/file` 读取插入源文件时使用的 file_id。 */
  projectRootPath: string
  /** 当前用户选中的文件项自带的 fileId，用于插入链路上下文与日志定位。 */
  selectedFileId: string
  /** 插入源 CAD 类型。 */
  cadType: 'cad_part' | 'cad_assembly'
  /** 插入对象展示名。 */
  displayName: string
  /** 插入时固定/浮动零部件策略，默认由 cad-core 在提交阶段归一到 SolidWorks 语义。 */
  fixationMode?: InsertComponentFixationMode
}

export interface LoadInsertedCadModelRequest {
  sessionId: string
  /** 插入链路注册导入 BRep Shape 的最小能力。 */
  brepRegistrar: BRepRegistrar
  descriptor: InsertCadSourceDescriptor
}

const INSERT_CACHE_VERSION = 'insert-cad-file-one-shot'
const INSERT_REFERENCE_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

interface ResolveInsertedReferenceFileIdInput {
  sourceFileId: string
  selectedFileId: string
  externalFileId?: string
  externalFileUuid: string
}

/**
 * 统一 Insert 链路中的文件路径分隔符。
 *
 * @param value 后端返回或 GLB 引用扩展中携带的文件标识。
 * @returns 去除首尾空白后的前端内部路径表达。
 */
function normalizeInsertedFilePath(value: string): string {
  return value.trim().replace(/\\/g, '/').replace(/^\.\/+/, '')
}

/**
 * 取得路径的父级目录。
 *
 * @param value 文件路径。
 * @returns 父级目录；没有父级时返回空字符串。
 */
function getInsertedFileDirname(value: string): string {
  const normalizedValue = normalizeInsertedFilePath(value)
  const lastSlashIndex = normalizedValue.lastIndexOf('/')
  return lastSlashIndex >= 0 ? normalizedValue.slice(0, lastSlashIndex) : ''
}

/**
 * 取得路径的最后一级名称。
 *
 * @param value 文件路径。
 * @returns 文件名或最后一级目录名。
 */
function getInsertedFileBasename(value: string): string {
  const normalizedValue = normalizeInsertedFilePath(value)
  const lastSlashIndex = normalizedValue.lastIndexOf('/')
  return lastSlashIndex >= 0 ? normalizedValue.slice(lastSlashIndex + 1) : normalizedValue
}

/**
 * 拼接 Insert 链路中的文件路径。
 *
 * @param base 父路径。
 * @param child 子路径。
 * @returns 使用 `/` 分隔的拼接结果。
 */
function joinInsertedFilePath(base: string, child: string): string {
  const normalizedBase = normalizeInsertedFilePath(base).replace(/\/+$/, '')
  const normalizedChild = normalizeInsertedFilePath(child).replace(/^\/+/, '')
  if (!normalizedBase) {
    return normalizedChild
  }
  if (!normalizedChild) {
    return normalizedBase
  }
  return `${normalizedBase}/${normalizedChild}`
}

/**
 * 判断 externalFileUuid 是否只是不可推导路径的 UUID 标识。
 *
 * @param value externalFileUuid 原始值。
 * @returns true 表示该值只有 UUID 语义，不能作为 `/download/file` 的 fileId 使用。
 */
function isInsertedReferenceUuidOnly(value: string): boolean {
  return INSERT_REFERENCE_UUID_PATTERN.test(normalizeInsertedFilePath(value))
}

/**
 * 判断 externalFileUuid 是否携带可用于拼接的 GLB 路径语义。
 *
 * @param value externalFileUuid 原始值。
 * @returns true 表示该值看起来是 GLB 文件路径或相对 GLB 文件名。
 */
function isInsertedReferenceGlbPath(value: string): boolean {
  const normalizedValue = normalizeInsertedFilePath(value)
  return normalizedValue.includes('/') || /\.glb$/i.test(normalizedValue)
}

/**
 * 根据当前 GLB fileId，为缺少 externalFileId 的子引用补齐路径型 fileId。
 *
 * 这里不推导 sidecar 文件，只恢复子 GLB 自身的路径身份；后续 BRep sidecar 仍由 cad-core
 * 根据 GLB 的 fileId 与 Insert 返回的 projectFiles 精确匹配。
 *
 * @param input 子引用与当前 GLB 上下文。
 * @returns 可传给 `/download/file` 和 cad-core 的子 GLB fileId；无法判断路径时返回空字符串。
 */
function resolveInsertedReferenceFileId(
  input: ResolveInsertedReferenceFileIdInput,
): string {
  const externalFileId = normalizeInsertedFilePath(input.externalFileId ?? '')
  if (externalFileId) {
    return externalFileId
  }

  const externalFileUuid = normalizeInsertedFilePath(input.externalFileUuid)
  if (!externalFileUuid) {
    return normalizeInsertedFilePath(input.selectedFileId)
  }

  if (isInsertedReferenceUuidOnly(externalFileUuid)) {
    return ''
  }

  if (!isInsertedReferenceGlbPath(externalFileUuid)) {
    return ''
  }

  const sourceFileId = normalizeInsertedFilePath(input.sourceFileId)
    || normalizeInsertedFilePath(input.selectedFileId)
  const sourceGlbStem = sourceFileId.replace(/\.glb$/i, '')
  const sourceGlbDirname = getInsertedFileDirname(sourceGlbStem)
  const sourceGlbBasename = getInsertedFileBasename(sourceGlbStem)

  if (!sourceGlbStem) {
    return externalFileUuid
  }

  if (externalFileUuid === sourceGlbStem || externalFileUuid.startsWith(`${sourceGlbStem}/`)) {
    return externalFileUuid
  }

  if (sourceGlbDirname && externalFileUuid.startsWith(`${sourceGlbDirname}/`)) {
    return externalFileUuid
  }

  if (
    sourceGlbBasename
    && (externalFileUuid === sourceGlbBasename
      || externalFileUuid.startsWith(`${sourceGlbBasename}/`))
  ) {
    return joinInsertedFilePath(sourceGlbDirname, externalFileUuid)
  }

  return joinInsertedFilePath(sourceGlbStem, externalFileUuid)
}

/**
 * 校验外部引用是否具备 `/download/file` 可读取的 fileId。
 *
 * @param fileId 已解析出的引用 fileId。
 * @param input 解析时使用的上下文，用于错误定位。
 */
function assertInsertedReferenceFileId(
  fileId: string,
  input: ResolveInsertedReferenceFileIdInput,
): void {
  if (fileId.trim()) {
    return
  }

  throw new Error(
    'insert-cad-loader: 插入装配体外部引用缺少可用于 /download/file 的 fileId，'
    + `selectedFileId=${input.selectedFileId}, sourceFileId=${input.sourceFileId}, `
    + `externalFileId=${input.externalFileId ?? ''}, externalFileUuid=${input.externalFileUuid}`,
  )
}

/**
 * 构造插入源引用解析器。
 * 说明：
 * 1. 根 GLB 已由插入预览下载结果进入 projectFiles；
 * 2. 子引用优先按 externalFileId 下载，缺少 fileId 但 externalFileUuid 是 GLB 路径时用当前 GLB fileId 补齐；
 * 3. 真正 UUID 型 externalFileUuid 直接明确报错，避免把 uuid 误当路径污染引用链路；
 * 4. 任一引用下载失败都抛出错误，交给上层 fail closed。
 */
function createInsertCadReferenceResolver(
  request: LoadInsertedCadModelRequest,
): ImportedAssemblyReferenceResolver {
  return {
    async loadReference(referenceRequest) {
      const referenceFileUuid = referenceRequest.externalFileUuid.trim()
      const referenceInput: ResolveInsertedReferenceFileIdInput = {
        sourceFileId: referenceRequest.sourceFileId,
        selectedFileId: request.descriptor.selectedFileId,
        externalFileId: referenceRequest.externalFileId,
        externalFileUuid: referenceFileUuid,
      }
      const referenceFileId = resolveInsertedReferenceFileId(referenceInput)
      assertInsertedReferenceFileId(referenceFileId, referenceInput)

      try {
        const bytes = await downloadInsertedFileByFileId(referenceFileId)
        if (!(bytes instanceof Uint8Array)) {
          logger.error('[insert-cad-loader] 引用文件下载结果无效', {
            selectedFileId: request.descriptor.selectedFileId,
            sourceFileId: referenceRequest.sourceFileId,
            resolvedReferenceFileId: referenceFileId,
            externalFileUuid: referenceRequest.externalFileUuid,
            externalFileId: referenceRequest.externalFileId,
          })
          throw new Error(`insert-cad-loader: 引用文件下载结果无效，fileId=${referenceFileId}`)
        }
        const referenceDisplayName = referenceFileId
          ? getInsertedFileBasename(referenceFileId)
          : ''
        return {
          file: {
            fileId: referenceFileId,
            uuid: referenceFileUuid || referenceFileId,
            kind: '.glb',
            displayName: referenceDisplayName || request.descriptor.displayName,
          } as unknown as FileInfo,
          bytes,
        }
      } catch (error) {
        logger.error('[insert-cad-loader] 引用文件下载失败', {
          selectedFileId: request.descriptor.selectedFileId,
          sourceFileId: referenceRequest.sourceFileId,
          resolvedReferenceFileId: referenceFileId,
          externalFileUuid: referenceRequest.externalFileUuid,
          externalFileId: referenceRequest.externalFileId,
          error,
        })
        throw error
      }
    },
  }
}

/**
 * 判断装配体结果是否仍带有未解析外部引用。
 * 一旦存在该类 warning，插入链路必须 fail closed，避免“设计树成功但场景无几何”。
 */
function hasUnresolvedInsertedAssemblyReferences(
  loaded: LoadedImportedModel,
): boolean {
  if (loaded.displayType !== 'assembly') {
    return false
  }
  if (loaded.result.kind !== 'composed-assembly') {
    return false
  }
  if (!Array.isArray(loaded.result.warnings)) {
    return false
  }
  return loaded.result.warnings.some((warning) => warning.includes('缺少外部'))
}

/**
 * 将插入文件类型转换成通用导入链路的显示语义。
 *
 * @param cadType 插入源 CAD 类型。
 * @returns 通用导入链路可识别的文档类型。
 */
function resolveInsertedKnownDisplayType(
  cadType: InsertCadSourceDescriptor['cadType'],
): 'assembly' | 'part' {
  return cadType === 'cad_assembly' ? 'assembly' : 'part'
}

interface InsertedRuntimePackageResolution {
  /** 由 fileId 扩展名解析出的插入类型。 */
  readonly kind: 'assembly' | 'part'
  /** 命中 `.part.cad` / `.assembly.cad` 的稳定 fileId。 */
  readonly fileId: string
}

/**
 * 解析插入 CAD 运行时包类型。
 *
 * 设计说明：只有 `.part.cad` / `.assembly.cad` 这类稳定 fileId 后缀进入新包加载分支；历史
 * `.cad_part` / `.cad_assembly` 或直接 GLB 下载继续走旧 loader，避免影响其它模块。
 *
 * @param request 插入加载请求。
 * @returns 可识别时返回零件或装配体类型，否则返回 null。
 */
function resolveInsertedRuntimePackage(
  request: LoadInsertedCadModelRequest,
): InsertedRuntimePackageResolution | null {
  const sourceFileId = [
    request.descriptor.selectedFileId,
    request.descriptor.projectRootPath,
  ].find((fileId) => isInsertedCadRuntimePackageFileId(fileId))

  if (!sourceFileId) {
    return null
  }

  return {
    kind: resolveInsertedCadKindFromFileId(sourceFileId),
    fileId: sourceFileId,
  }
}

/**
 * 解析插入链路必需的 BRep Shape 注册能力。
 *
 * 设计说明：这里在下载根文件前 fail-fast，避免缺少 registrar 时已经产生下载、解析或缓存写入副作用。
 *
 * @param request 插入 CAD 模型加载请求。
 * @returns 可用于 cad-core hydration 的 BRep registrar。
 */
function requireInsertedCadBRepRegistrar(
  request: LoadInsertedCadModelRequest,
): BRepRegistrar {
  if (request.brepRegistrar) {
    return request.brepRegistrar
  }

  throw new Error('insert-cad-loader: 缺少 brepRegistrar，无法注册插入 CAD 的 BRep Shape')
}

/**
 * 加载待插入的 CAD 模型。
 *
 * @param request 插入 CAD 模型加载请求。
 * @returns 已解析的导入模型；解析或装配加载失败时返回 null。
 * @throws 当根文件下载阶段无法定位或读取文件时抛出原始错误，供上层直接提示。
 */
export async function loadInsertedCadModel(
  request: LoadInsertedCadModelRequest,
): Promise<LoadedImportedModel | null> {
  const brepRegistrar = requireInsertedCadBRepRegistrar(request)
  let insertedCadFile: DownloadInsertedCadFileResult
  try {
    insertedCadFile = await downloadInsertedCadFile({
      sessionId: request.sessionId,
      projectRootPath: request.descriptor.projectRootPath,
      type: request.descriptor.cadType,
    })
  } catch (error) {
    logger.error('[insert-cad-loader] 下载失败', error)
    throw error
  }
  const referenceResolver = createInsertCadReferenceResolver(request)

  // 插入链路使用一次性缓存隔离主工程缓存；返回结果由上层接管或销毁。
  // 后续需要把模板资产缓存的清理策略收口到插入管线的生命周期规范中。
  const parsedGlbCache = new ParsedGlbCache()
  const partAssetCache = new PartAssetCache()
  const assemblyManifestCache = new AssemblyManifestCache()
  const assemblyModelCache = new AssemblyModelCache()
  const importedBrepRuntimeCache = createImportedBrepRuntimeCache()
  const transaction = new ImportedModelLoadTransaction({
    partAssetCache,
    assemblyModelCache,
    parsedGlbCache,
  })
  const runtimePackage = resolveInsertedRuntimePackage(request)

  if (runtimePackage) {
    try {
      const loaded = await loadInsertedCadRuntimePackageModel({
        bytes: insertedCadFile.bytes,
        displayName: request.descriptor.displayName,
        kind: runtimePackage.kind,
        selectedFileId: runtimePackage.fileId,
        sessionId: request.sessionId,
        assemblyManifestCache,
        assemblyModelCache,
        brepRegistrar,
        importedBrepRuntimeCache,
        parsedGlbCache,
        partAssetCache,
        transaction,
      })
      transaction.commit()
      return loaded
    } catch (error) {
      transaction.abort()
      logger.error('[insert-cad-loader] 插入 CAD 运行时包加载失败', {
        selectedFileId: request.descriptor.selectedFileId,
        projectRootPath: request.descriptor.projectRootPath,
        runtimePackageKind: runtimePackage.kind,
        error,
      })
      throw error
    }
  }

  // 先解析并写入 parsedGlbCache，让 loadImportedModel 复用同一份 GLTF；
  // 若后续发生 cache miss，也能使用真实 sessionId 与后端地址进入通用 loader 兜底链路。
  try {
    const loader = new GLTFLoader()
    const gltf = await parseGlb(loader, insertedCadFile.bytes)
    parsedGlbCache.set(insertedCadFile.glbFile.uuid, INSERT_CACHE_VERSION, gltf)
  } catch (error) {
    transaction.abort()
    logger.error('[insert-cad-loader] parseGlb 失败', error)
    return null
  }

  // Insert 路径使用一次性隔离 cache group 与一次性 transaction：与主链路共享 transaction
  // 完全解耦，避免 Insert 失败 / 被取消时影响主编辑会话的 staging / commit。
  try {
    // 故意不传 requestRegistry：insert-cad 流水线使用一次性隔离 cache / transaction，
    // 没有并发 caller 需要 dedup，引入共享 registry 反而会把跨 tab 状态漏进插入链路。
    const loaded = await loadImportedModel(request.sessionId, insertedCadFile.glbFile, {
      projectFiles: insertedCadFile.projectFiles,
      version: INSERT_CACHE_VERSION,
      assemblyModelCache,
      partAssetCache,
      assemblyManifestCache,
      parsedGlbCache,
      importedBrepRuntimeCache,
      brepRegistrar,
      knownDisplayType: resolveInsertedKnownDisplayType(request.descriptor.cadType),
      selectedFileId: request.descriptor.selectedFileId,
      referenceResolver,
      transaction,
    })
    if (!loaded) {
      transaction.abort()
      return null
    }
    if (hasUnresolvedInsertedAssemblyReferences(loaded)) {
      logger.error('[insert-cad-loader] 装配体存在未解析外部引用，拒绝继续插入', {
        projectRootPath: request.descriptor.projectRootPath,
        warnings: loaded.result.kind === 'composed-assembly' ? loaded.result.warnings : [],
      })
      try {
        disposeImportedModel(loaded.result)
      } catch (disposeError) {
        logger.error('[insert-cad-loader] unresolved-reference dispose 失败', disposeError)
      }
      transaction.abort()
      return null
    }
    // 成功路径：commit 把 staged asset 写入隔离 cache。虽然 cache 很快就被抛弃，但走
    // transaction 路径保证 loader 契约在所有 consumer（主链路 + Insert）一致。
    transaction.commit()
    return loaded
  } catch (error) {
    transaction.abort()
    logger.error('[insert-cad-loader] loadImportedModel 失败', error)
    return null
  }
}
